import { cn } from "@/lib/utils";
import { LucideIcon, TrendingUp, TrendingDown, Minus } from "lucide-react";

interface MetricCardProps {
  title: string;
  value: string | number;
  change?: number;
  changeLabel?: string;
  icon: LucideIcon;
  variant?: "default" | "primary" | "success" | "warning";
}

export function MetricCard({
  title,
  value,
  change,
  changeLabel,
  icon: Icon,
  variant = "default",
}: MetricCardProps) {
  const isPositive = change && change > 0;
  const isNegative = change && change < 0;
  const isNeutral = change === 0;

  return (
    <div className="metric-card card-glow group relative">
      {/* Subtle top gradient line */}
      <div 
        className={cn(
          "absolute top-0 left-0 right-0 h-[2px] rounded-t-xl opacity-60",
          variant === "primary" && "bg-gradient-to-r from-transparent via-primary to-transparent",
          variant === "success" && "bg-gradient-to-r from-transparent via-success to-transparent",
          variant === "warning" && "bg-gradient-to-r from-transparent via-warning to-transparent",
          variant === "default" && "bg-gradient-to-r from-transparent via-muted-foreground/30 to-transparent"
        )}
      />
      
      <div className="flex items-start justify-between">
        <div className="space-y-1.5">
          <p className="text-sm font-medium text-muted-foreground tracking-wide uppercase text-[11px]">
            {title}
          </p>
          <p className="text-3xl font-bold tracking-tight bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text">
            {value}
          </p>
        </div>
        <div
          className={cn(
            "p-3 rounded-xl transition-all duration-300 relative overflow-hidden",
            variant === "primary" && "bg-primary/10 text-primary",
            variant === "success" && "bg-success/10 text-success",
            variant === "warning" && "bg-warning/10 text-warning",
            variant === "default" && "bg-muted text-muted-foreground"
          )}
        >
          {/* Icon glow effect on hover */}
          <div 
            className={cn(
              "absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl",
              variant === "primary" && "bg-primary/20",
              variant === "success" && "bg-success/20",
              variant === "warning" && "bg-warning/20"
            )}
          />
          <Icon className="h-5 w-5 relative z-10" />
        </div>
      </div>
      
      {change !== undefined && (
        <div className="mt-4 pt-3 border-t border-border/30 flex items-center gap-2">
          <div 
            className={cn(
              "flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold",
              isPositive && "bg-success/10 text-success",
              isNegative && "bg-destructive/10 text-destructive",
              isNeutral && "bg-muted text-muted-foreground"
            )}
          >
            {isPositive && <TrendingUp className="h-3 w-3" />}
            {isNegative && <TrendingDown className="h-3 w-3" />}
            {isNeutral && <Minus className="h-3 w-3" />}
            <span>
              {isPositive && "+"}
              {change}%
            </span>
          </div>
          {changeLabel && (
            <span className="text-xs text-muted-foreground">{changeLabel}</span>
          )}
        </div>
      )}
    </div>
  );
}
